/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import * as api from '@/api/play';
import { PLAY } from '@/store/types';
import { equals } from '@/utils/util';
import { finalizeAction, StoreActionEnv, ActionType } from '@/store/actions';
import { PlayService } from '@/api/types/play';

export interface StorePlayServiceListState {
  engine: string;
  engineVal: string;
  sort: string;
  page: number;
  gender: number;
  filter: Record<string, string | string[]>;
  list: PlayService[];
  noMore: boolean;
  updateTime: number;
  lock: boolean;
}

export interface StorePlayServiceListGetters {
  engine: string;
}

export default {
  namespaced: true,
  state: {
    engine: null,
    engineVal: null,
    sort: null,
    page: 0,
    gender: null,
    filter: {},
    list: [],
    noMore: null,
    updateTime: null,
    lock: null,
  },
  getters: {
    engine(state: StorePlayServiceListState) {
      return state.engineVal || state.engine;
    },
  },
  actions: {
    [PLAY.GET_SERVICE_LIST](
      { state, commit }: StoreActionEnv<StorePlayServiceListState>,
      { engine, sort, gender, filter: filterData = {}, action: rawAction, silent }: {
        engine: string;
        sort: string;
        gender: number;
        filter: Record<string, string | string[]>;
        action: ActionType;
        silent: boolean;
      },
    ) {
      const filter = {};
      Object.entries(filterData).forEach(([k, v]) => {
        const sv = v instanceof Array ? v.join(',') : v;
        if (sv) {
          filter[k] = sv;
        }
      });
      const action = finalizeAction(rawAction, state.engine === engine
        && state.sort === sort && state.gender === gender
        && equals(state.filter, filter, { ignoreValues: [void 0, null] }));
      if (action && !state.lock) {
        commit(PLAY.GET_SERVICE_LIST, { status: 'start' });
        const page = action === 'reload' ? 1 : state.page + 1;
        return new Promise((resolve, reject) => {
          const params = { sort, page, gender, filter };
          Object.entries(filter).forEach(([k, v]) => {
            params[`filter[${k}]`] = v;
          });
          api.getServices(engine, params, silent).then((res) => {
            commit(PLAY.GET_SERVICE_LIST, {
              status: 'success',
              action,
              engine,
              sort,
              page,
              gender,
              filter,
              data: res.data,
              engineVal: res.extra.engine,
              pageCount: res.extra.pageCount,
            });
            resolve();
          }).catch((err) => {
            commit(PLAY.GET_SERVICE_LIST, { action: 'failed' });
            reject(err);
          });
        });
      }
      return Promise.resolve();
    },
  },
  mutations: {
    [PLAY.GET_SERVICE_LIST](
      state: StorePlayServiceListState,
      { status, action, engine, sort, page, gender, filter, data, engineVal, pageCount },
    ) {
      if (status === 'start') {
        state.lock = true;
      } else {
        if (status === 'success') {
          state.engine = engine;
          state.sort = sort;
          state.page = page;
          state.gender = gender;
          state.filter = filter;
          state.list = action === 'reload' || page === 0 ? data : state.list.concat(data);
          state.engineVal = engineVal;
          state.noMore = page >= pageCount;
          state.updateTime = new Date().valueOf();
        }
        state.lock = false;
      }
    },
  },
};
